import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `trace` em português do Brasil. Declarado, nunca inferido, então uma lacuna quebra o `tsc`.
 *
 * ── O registro que PODE falar de mecanismo ──
 *
 * Aqui é o painel de quem opera a instalação, e é o único lugar onde `léxico`, `semântico`,
 * `recuperado`, `modelo` e a taxonomia de confiança aparecem. A coluna da conversa
 * (`chat`) guarda a distância oposta, em português exatamente como em inglês (AGM-022).
 *
 * Termos de arte ficam em inglês dentro da frase em português, de propósito (D-15): `tok`,
 * `ttft`, `cache`. Quem opera a plataforma lê essas palavras em inglês em todas as
 * ferramentas que usa, e "memória temporária" seria menos claro, não mais.
 *
 * ── Gênero ──
 *
 * `execução` é feminino, então todo `runStatus` concorda no feminino: "concluída",
 * "suspensa", "cancelada". As seis classes de risco concordam com `ação`, também feminino.
 * `nodeFailed` usa o verbo "falhou" em vez de um adjetivo justamente para não ter de
 * concordar com uma `etapa` num lugar e com uma `execução` no outro.
 */
export const trace: Messages['trace'] = {
  panelLabel: 'Rastro da execução',
  title: 'Rastro',
  inProgress: 'Execução em andamento',
  totalTokens: 'tok',
  totalCost: 'custo',
  close: 'Fechar o rastro da execução',
  empty:
    'Cada chamada de modelo, busca e uso de ferramenta aparece aqui conforme acontece — com o modelo que executou, o que ele recuperou e quanto custou.',

  /**
   * A legenda de uma etapa, vinda do código que o servidor nomeou e não da prosa que ele
   * escreveu (D-8). Este é o registro de quem OPERA: `model_call` diz qual perfil de modelo
   * a instalação escolheu, coisa que `chat.stepActivity` não pode dizer (AGM-022).
   *
   * O alias do modelo é dado e nunca se traduz (D-17). `base de conhecimento` é feminino,
   * `agente` é masculino — "um agente remoto" —, e as demais são infinitivos, que não
   * concordam com nada.
   */
  stepLabel: {
    model_call: ({ alias }) => `Raciocinando com ${alias}`,
    knowledge_search: () => 'Buscar na base de conhecimento',
    memory_write: () => 'Guardar uma preferência',
    announcement: () => 'Enviar comunicado',
    delegation: () => 'Delegar a um agente remoto',
    /** Nome escrito por um servidor que não é desta plataforma: dado, exibido tal e qual. */
    tool_call: ({ tool }) => tool
  },

  risk: {
    read: 'leitura',
    reversible_write: 'escrita reversível',
    external_communication: 'comunicação externa',
    privileged: 'privilegiada',
    financial: 'financeira',
    destructive: 'destrutiva'
  },
  riskUnknown: 'sem classificação',

  /** Zero fica no singular em português: "0 trecho". */
  passages: (fmt, kept) =>
    pluralCategory(fmt.locale, kept) === 'one' ? `${fmt.formatCount(kept)} trecho` : `${fmt.formatCount(kept)} trechos`,

  nodeFailed: 'falhou',
  toolReached: (toolName) => `→ ${toolName}`,
  nodeError: (code, message) => `${code}: ${message}`,

  modelIn: 'entrada',
  modelOut: 'saída',
  modelCached: 'cache',
  modelTtft: 'ttft',
  modelStop: 'parada',

  contextTitle: 'Contexto montado',
  contextTokens: (tokens) => `~${tokens}`,

  trust: {
    platform_instruction: 'instrução da plataforma',
    skill_instruction: 'instrução da habilidade',
    /** "pessoa", não *usuário*: aqui se trata de um ser humano (ADR-0018). */
    user_input: 'entrada da pessoa',
    user_memory: 'memória da pessoa',
    retrieved_document: 'documento recuperado',
    tool_result: 'resultado da ferramenta',
    remote_agent_content: 'conteúdo de agente remoto'
  },
  trustUnknown: 'sem rótulo',

  toolsOffered: (tools) => `ferramentas oferecidas: ${tools}`,

  retrievalLexical: 'léxicos',
  retrievalSemantic: 'semânticos',
  retrievalFused: 'fundidos',
  retrievalKept: 'mantidos',

  resultMarker: (position) => `[${position}]`,
  resultLexicalRank: (rank) => `léx #${rank}`,
  resultSemanticRank: (rank) => `sem #${rank}`,

  toolResult: (summary) => `→ ${summary}`,

  budgetTitle: 'Perto do orçamento',
  dimension: {
    steps: 'etapas',
    model_calls: 'chamadas de modelo',
    tool_calls: 'chamadas de ferramenta',
    cost_usd: 'custo',
    wall_clock_ms: 'tempo'
  },
  dimensionUnknown: 'orçamento',
  budgetAmount: (used, limit) => `${used} de ${limit}`,

  outcomeTitle: 'Resultado',
  runStatus: {
    queued: 'na fila',
    running: 'em andamento',
    waiting_for_approval: 'aguardando aprovação',
    waiting_for_input: 'aguardando entrada',
    waiting_for_external: 'aguardando sistema externo',
    suspended: 'suspensa',
    completed: 'concluída',
    partially_completed: 'parcialmente concluída',
    failed: 'falhou',
    cancelled: 'cancelada',
    expired: 'expirada'
  },

  outcomeSteps: 'etapas',
  outcomeModel: 'modelo',
  outcomeTools: 'ferramentas',
  outcomeTime: 'tempo',
  outcomeCost: 'custo',

  outcomeCompleted: 'Concluído',
  outcomeIncomplete: 'Não concluído',
  outcomeCaveats: 'Ressalvas'
}
